import { useRef } from 'react'
import { Film } from 'lucide-react'
import { formatBytes } from '@/utils/formatBytes'
import { MediaDownloadButton } from './MediaDownloadButton'
import { useSeenOnce } from './lazyObserver'
import { usePlayableLink } from './mediaLink'
import { MEDIA_TEXT } from './mediaViewText'

/** SA-1094 (ruling M1): the largest video that plays in the bubble. Above it,
 * the view button and the dialog. */
export const INLINE_VIDEO_MAX_BYTES = 100 * 1024 * 1024

export interface InlineVideoProps {
  messageId: string
  mediaUrl: string
  filename?: string | null
  byteSize?: number | null
}

/**
 * SA-1094 (design note §4.2): a video plays in the bubble. Nothing is minted
 * until the element scrolls into view (the pane's one shared observer,
 * `lazyObserver.useSeenOnce`; at once where there is none). The player loads only its metadata, and the
 * `#t=0.001` fragment makes Safari paint the first frame as Chrome does; the
 * fragment never reaches the server. A failure before the link expired is a
 * file this browser cannot play: the player gives way to the download
 * button. After expiry the link renews once (`usePlayableLink`).
 */
export function InlineVideo({ messageId, mediaUrl, filename, byteSize }: InlineVideoProps) {
  const ref = useRef<HTMLDivElement>(null)
  const seen = useSeenOnce(ref)

  const { src, status, retry, onError, onLoadedMetadata } = usePlayableLink(mediaUrl, { enabled: seen })
  const name = filename || MEDIA_TEXT.videoPlaceholder

  if (status === 'unplayable') {
    return <MediaDownloadButton messageId={messageId} mediaUrl={mediaUrl} filename={filename} byteSize={byteSize} />
  }
  if (status === 'gone') {
    return <p className="text-xs text-muted-foreground italic mb-1">{MEDIA_TEXT.gone}</p>
  }
  if (status === 'failed') {
    return (
      <div className="mb-1">
        <p className="text-xs text-destructive italic">{MEDIA_TEXT.failed}</p>
        <button type="button" onClick={retry} className="text-xs text-primary underline">
          {MEDIA_TEXT.tryAgain}
        </button>
      </div>
    )
  }
  return (
    <div ref={ref} className="mb-1 w-full" data-testid="inline-video">
      {status === 'ready' && src ? (
        <video
          src={`${src}#t=0.001`}
          preload="metadata"
          controls
          aria-label={name}
          playsInline
          data-testid="inline-video-player"
          className="w-full max-h-[320px] rounded-lg border border-border bg-black"
          onError={(e) => { void onError(e.currentTarget) }}
          onLoadedMetadata={(e) => onLoadedMetadata(e.currentTarget)}
        />
      ) : (
        <div
          data-testid="inline-video-placeholder"
          className="w-full aspect-video max-h-[320px] rounded-lg border border-border bg-muted flex items-center justify-center"
          aria-hidden="true"
        >
          <Film className="w-6 h-6 text-muted-foreground" />
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        {name}
        {byteSize != null && <span> · {formatBytes(byteSize)}</span>}
      </p>
    </div>
  )
}
